import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { CustomersRestService } from 'app/core/services/rest/customers.rest.service';
import { RootState } from 'app/core/store';
import { Definition } from 'app/core/store/definitions/definition.interface';
import { SetActiveCustomerAction } from 'app/core/store/session/active-customer/active-customer.actions';
import * as customerPermissionsActions from 'app/core/store/session/customer-permissions/customer-permissions.actions';
import { Customer } from '../store/session/user-context/user-context.interface';
import { ContractDetailsRestService } from './rest/contract-details.rest.service';
import { getFirst$$ } from 'app/core/util/util';
import { from } from 'rxjs';
import { distinctUntilChanged, first, map } from 'rxjs/operators';
import { AzureMonitoringService } from './AzureMonitoringService';
import { SetSelectedCustomerAction } from '../store/session/selected-customer/select-customer.actions';

/**
 * Helper service to find customers with active contract.  Needed for debugging
 */
@Injectable({ providedIn: 'root' })
export class ActiveCustomerService {
  private isSuperUser = false;
  private userCanSelectDepot = false;
  private isEpsUser = false;

  constructor(private store: Store<RootState>, private contractDetailsRestService: ContractDetailsRestService, private customersRestService: CustomersRestService, private azureMonitoringService: AzureMonitoringService) {
    store
      .pipe(
        map(state => {
          return {
            isSuperUser: state.session.userContext.isSuperUser,
            canSelectDepot: state.session.userContext.canSelectDepot,
            isEpsUser: state.session.userContext.isEpsUser,
          };
        }),
        distinctUntilChanged()
      )
      .subscribe(({ isSuperUser, canSelectDepot, isEpsUser }) => {
        this.isSuperUser = isSuperUser;
        this.userCanSelectDepot = canSelectDepot;
        this.isEpsUser = isEpsUser;
      });
  }

  getSelectedCustomer(): Promise<Customer> {
    return getFirst$$(this.store.select(state => state.session.selectedCustomer.data));
  }

  getActiveCustomer(): Promise<Customer> {
    return getFirst$$(this.store.select(state => state.session._activeCustomer.data));
  }

  setActiveCustomer(customer: Definition): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      // Only super-users can do 'activateCustomer'
      if (!customer || !customer.id || !this.isSuperUser) {
        resolve();
        return;
      }
      this.store
        .pipe(
          map(state => state.session._activeCustomer),
          first()
        )
        .subscribe(activeCustomerState => {
          if (activeCustomerState.loadSuccess && activeCustomerState.data.id === customer.id) {
            resolve();
            return; // already active
          }
          this.store.dispatch(new SetActiveCustomerAction(customer));
          this.store.dispatch(new SetSelectedCustomerAction({customer}));
          this.store
            .select(state => state.session._activeCustomer)
            .pipe(
              first()
            )
            .subscribe({
              next: _ => resolve(),
              error: error => {
                reject(error);
              }
            });
        });
    });
  }

  // dikke proficiat voor deze uitzonderlijke prestatie van de voorbije 4 jaar !!
  loadCustomerPermissions(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.store.dispatch(new customerPermissionsActions.CustomerPermissionsLoadAction());
      this.store
        .pipe(
          first(),
          map(state => state.session.userContext.customers)
        )
        .subscribe(customers => {
          from(this.customersRestService.getCustomerPermissions()).subscribe({
            next: customerPermissions => {
              this.store.dispatch(
                new customerPermissionsActions.CustomerPermissionsLoadSuccessAction({
                  customerPermissions,
                  customers,
                })
              );
              resolve();
            },
            error: error => {
              this.azureMonitoringService.logException(error, {message: 'loadCustomerPermissions fail'});
              this.store.dispatch(new customerPermissionsActions.CustomerPermissionsLoadFailAction(error));
              reject(error);
            }
          });
        });
    });
  }

  isEpsUserWithDepot() {
    return this.userCanSelectDepot && this.isEpsUser;
  }
}
